// Shared primitives for Ledger views (Procurement, Billing Summaries,
// future Receipts). Established as the single source of truth for the
// simplified-cell pattern: dollar amounts are the visual anchor,
// status communicated by color, supporting detail in hover tooltips.
//
// Exports (grouped):
//   Constants:    STICKY_BG, GROUP_BG, HEADER_BG, ROW_BG_A, ROW_BG_B,
//                 FOOTER_BG, CARD_BORDER, CARD_RADIUS, W, td, th, thRight
//   Formatters:   fmt$, fmtShortDate
//   Helpers:      cellWidth, stickyLeft, stickyRight, ColGroup
//   Components:   TooltipHeading, TooltipRow, AmountCell,
//                 FeesCell, PaymentBlocks
//   Classifiers:  depositState, finalState, poState, venFinalState
//                 amountStatusColor, statusLabel
//   Lookup:       clientPaymentBlocksFor, vendorPaymentBlocksFor
//
// Cell template: every lifecycle cell renders one dollar amount with
// optional QB ✓ indicator. State (paid/partial/unpaid/projected/neutral)
// drives the amount color. Hover triggers a tooltip with ref/dates/QB
// id/push button. Empty state: centered em-dash at line-2 Y position.

import React from 'react';
import { parseQty, itemFeeBreakdown, itemFeeMarkupPct, customFeeMarkupPct } from '../../utils';
import LedgerHoverTooltip from './LedgerHoverTooltip';

// ── Constants ───────────────────────────────────────────────────────────
export var STICKY_BG = '#FFFFFF';
export var GROUP_BG  = '#F5F5F2';
export var HEADER_BG = '#F7F7F5';
export var ROW_BG_A  = '#FFFFFF';
export var ROW_BG_B  = '#FAFAF8';
export var FOOTER_BG = '#FAFAF8';
export var CARD_BORDER = '0.5px solid #D8D7D2';
export var CARD_RADIUS = 6;

// FEES widened 110 → 150 (2026-05-26) — the FeesCell trigger renders
// "$X,XXX.XX" in mono-bold 13pt PLUS a 4pt-margin disclosure triangle,
// which pushed total content width past 110pt on cents-included values
// like "$17,132.95" and clipped the cell. The other right-aligned
// columns (cliOut, venOut) render bare amounts with no triangle and
// fit comfortably in 110pt for the project's value range.
export var W = {
  product: 240,
  deposit: 140, po: 130, final: 140, venFinal: 120,
  delivered: 100, fees: 150,
  cliOut: 110, venOut: 110,
};

export var th = {
  padding: '10px 14px', textAlign: 'left',
  fontSize: 11, fontWeight: 600, color: '#444',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  borderBottom: '0.5px solid #D8D7D2', background: HEADER_BG,
  whiteSpace: 'nowrap', verticalAlign: 'top',
};
export var thRight = Object.assign({}, th, { textAlign: 'right' });

export var td = {
  padding: '10px 14px',
  color: '#111', fontSize: 12, whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

// ── Formatters ──────────────────────────────────────────────────────────
export var fmt$ = function(n) {
  var v = parseFloat(n) || 0;
  if (v === 0) return '$0.00';
  return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// M/D/YY (e.g. 1/8/26). Single canonical date format for every Ledger
// view. Accepts ISO or timestamp; falls back to the raw string if it
// can't be parsed (defensive — would rather show something than swallow
// data).
export var fmtShortDate = function(d) {
  if (!d) return '';
  var s = String(d).slice(0, 10);
  var parts = s.split('-');
  if (parts.length !== 3) return s;
  var yy = parts[0].slice(2);
  var mm = parseInt(parts[1], 10);
  var dd = parseInt(parts[2], 10);
  if (isNaN(mm) || isNaN(dd)) return s;
  return mm + '/' + dd + '/' + yy;
};

// ── Layout helpers ──────────────────────────────────────────────────────
export function cellWidth(w) { return { minWidth: w, maxWidth: w, width: w }; }
export function stickyLeft(offset, bg)  { return { position: 'sticky', left: offset,  background: bg || STICKY_BG, zIndex: 1 }; }
export function stickyRight(offset, bg) { return { position: 'sticky', right: offset, background: bg || STICKY_BG, zIndex: 1 }; }

// Shared colgroup definition — every table that renders the 9-column
// lifecycle layout uses ColGroup to keep widths consistent so columns
// align across the sticky header, the per-proposal item tables, and
// the sticky footer.
export function ColGroup() {
  return (
    <colgroup>
      <col style={{ width: W.product }} />
      <col style={{ width: W.deposit }} />
      <col style={{ width: W.final }} />
      <col style={{ width: W.po }} />
      <col style={{ width: W.venFinal }} />
      <col style={{ width: W.delivered }} />
      <col style={{ width: W.fees }} />
      <col style={{ width: W.cliOut }} />
      <col style={{ width: W.venOut }} />
    </colgroup>
  );
}

// ── Tooltip subcomponents ───────────────────────────────────────────────
export function TooltipHeading(p) {
  return (
    <div style={{ fontSize: 9, fontWeight: 600, color: '#888',
      textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
      {p.children}
    </div>
  );
}

export function TooltipRow(p) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: 12, fontSize: 11, padding: '3px 0' }}>
      <span style={{ color: '#666' }}>{p.label}</span>
      <span style={{
        color: '#111',
        fontFamily: p.mono ? 'monospace' : 'inherit',
        cursor: p.onClick ? 'pointer' : 'default',
        textDecoration: p.onClick ? 'underline dotted' : 'none',
        textAlign: 'right',
      }} onClick={p.onClick || null}>
        {p.value}
      </span>
    </div>
  );
}

// ── Color + status classifiers ──────────────────────────────────────────
export function amountStatusColor(state) {
  if (state === 'paid')      return '#1A5C38';
  if (state === 'partial')   return '#BF6800';
  if (state === 'unpaid')    return '#D4007A';
  if (state === 'projected') return '#888';
  return '#111';
}

export function statusLabel(s) {
  if (s === 'paid')      return 'Paid';
  if (s === 'partial')   return 'Partial';
  if (s === 'unpaid')    return 'Unpaid';
  if (s === 'projected') return 'Projected';
  if (s === 'neutral')   return 'Awaiting';
  return '';
}

// Per-cell state classifiers — same rules apply uniformly across views.
//   'empty'     — no document AND no projection
//   'projected' — no document but a future amount expected (muted gray)
//   'unpaid'    — billed, nothing paid yet (fuchsia)
//   'partial'   — billed, some paid (amber)
//   'paid'      — billed, fully paid (green)
//   'neutral'   — billed, payment not applicable / not started (black)
export function depositState(st) {
  if (!st.depositInvoice) return st.itemDepositExpected > 0.01 ? 'projected' : 'empty';
  if (st.itemDepositUnpaid <= 0.01) return 'paid';
  if (st.itemDepositPaid > 0.01)    return 'partial';
  return 'unpaid';
}
export function finalState(st) {
  if (!st.finalInvoice) return st.itemFinalExpected > 0.01 ? 'projected' : 'empty';
  if (st.itemFinalUnpaid <= 0.01) return 'paid';
  if (st.itemFinalPaid > 0.01)    return 'partial';
  return 'unpaid';
}
export function poState(st, item) {
  if (!st.po) return 'empty';
  var paid = st.itemVendorDepositPaid || 0;
  if (paid <= 0.01) return 'neutral';
  var qty = parseQty(item.qty) || 1;
  var expected = (parseFloat(item.netPrice) || 0) * qty * (parseFloat(item.depositPct) || 50) / 100;
  if (expected <= 0.01) return paid > 0 ? 'paid' : 'neutral';
  if (paid >= expected - 0.01) return 'paid';
  return 'partial';
}
export function venFinalState(st) {
  if ((st.itemVendorFinalPaid || 0) > 0.01) return 'paid';
  if (!st.po) return 'empty';
  var projected = (st.net || 0) - (st.itemVendorDepositPaid || 0);
  if (projected > 0.01) return 'projected';
  return 'empty';
}

// ── AmountCell — the canonical lifecycle cell ───────────────────────────
// One dollar amount (stacked paid/of-expected for partial), tiny QB ✓
// when the underlying record is in QB, and a hover tooltip for the rest.
export function AmountCell(p) {
  if (p.state === 'empty' || (p.amount == null)) {
    return (
      <div style={{ textAlign: p.align || 'right', color: '#bbb',
        fontFamily: 'monospace', fontSize: 14 }}>{'\u2014'}</div>
    );
  }
  var color = amountStatusColor(p.state);
  var primary = (
    <span style={{ fontFamily: 'monospace', fontWeight: 600,
      fontSize: 13, color: color, whiteSpace: 'nowrap' }}>
      {fmt$(p.amount)}
    </span>
  );
  var body = p.partialOf != null ? (
    <div style={{ textAlign: p.align || 'right' }}>
      {primary}
      <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace',
        marginTop: 1, whiteSpace: 'nowrap' }}>
        of {fmt$(p.partialOf)}
      </div>
    </div>
  ) : (
    <div style={{ textAlign: p.align || 'right' }}>{primary}</div>
  );
  if (!p.tooltip) return body;
  return (
    <LedgerHoverTooltip content={p.tooltip} position={p.tooltipPosition} width={p.tooltipWidth}>
      {body}
    </LedgerHoverTooltip>
  );
}

// ── Payment metadata block lookup (pure helpers) ────────────────────────
// Build payment metadata blocks from raw allocations/payments data.
// Caller supplies the relevant arrays (paymentAllocations + clientPayments,
// or vendorPayments) so this stays decoupled from useAppContext.

export function clientPaymentBlocksFor(documentType, documentId, paymentAllocations, clientPayments) {
  if (!documentId) return [];
  var allocs = (paymentAllocations || []).filter(function(a) {
    var t = a.documentType || a.document_type;
    var d = a.documentId != null ? a.documentId : a.document_id;
    return t === documentType && String(d) === String(documentId);
  });
  return allocs.map(function(a) {
    var pmt = (clientPayments || []).find(function(p) { return p.id === (a.paymentId || a.payment_id); }) || {};
    return {
      paymentId: pmt.id || a.paymentId || a.payment_id,
      date:      pmt.paymentDate || pmt.payment_date || a.createdAt || a.created_at || '',
      method:    pmt.method || '',
      reference: pmt.reference || pmt.bankReference || pmt.bank_reference || '',
      notes:     pmt.notes || '',
      amount:    parseFloat(a.amount) || 0,
    };
  }).sort(function(a, b) {
    return String(b.date || '').localeCompare(String(a.date || ''));
  });
}

export function vendorPaymentBlocksFor(poId, paymentType, vendorPayments) {
  if (!poId) return [];
  return (vendorPayments || [])
    .filter(function(vp) {
      if (vp.poId !== poId) return false;
      if (paymentType && vp.paymentType !== paymentType) return false;
      return true;
    })
    .map(function(vp) {
      return {
        paymentId: vp.id,
        date:      vp.paymentDate || '',
        method:    '',
        reference: vp.reference || '',
        notes:     vp.notes || '',
        amount:    parseFloat(vp.amount) || 0,
        paymentType: vp.paymentType || '',
      };
    })
    .sort(function(a, b) {
      return String(b.date || '').localeCompare(String(a.date || ''));
    });
}

// Renders a stack of payment metadata blocks (amount / received /
// method / reference / notes / QB id). Multiple payments separated by
// hairlines, newest first. Called as a function (positional arg) so
// existing call sites work unchanged: `{PaymentBlocks(blocks)}`.
export function PaymentBlocks(blocks) {
  if (!blocks || blocks.length === 0) return null;
  return blocks.map(function(pb, i) {
    return (
      <div key={pb.paymentId || i} style={{ marginTop: 8, paddingTop: 8, borderTop: '0.5px solid #E0E0DC' }}>
        {pb.amount > 0 && <TooltipRow label="Amount" mono value={fmt$(pb.amount)} />}
        {pb.date     && <TooltipRow label="Received" value={fmtShortDate(pb.date)} />}
        {pb.method   && <TooltipRow label="Method"   value={pb.method} />}
        {pb.reference && <TooltipRow label="Reference" value={pb.reference} mono />}
        {pb.notes    && <TooltipRow label="Notes"    value={pb.notes} />}
      </div>
    );
  });
}

// ── FEES cell — hover-based breakdown with profit column ────────────────
// Trigger shows the marked-up total ($ + chevron); hover opens a
// breakdown panel: Fee / Formula / Result / Profit. Tax override 🔒
// indicator preserved.
export function FeesCell(p) {
  var fees = (p.item && p.item.fees) || {};
  var total = p.total || 0;
  var taxOverride = !!p.item.taxManualOverride;

  if (total === 0) {
    return (
      <div style={{ textAlign: 'right', color: '#bbb',
        fontFamily: 'monospace', fontSize: 14 }}>{'\u2014'}</div>
    );
  }

  function feeRow(label, raw, markup, marginRate, isOverride) {
    var n = parseFloat(raw) || 0;
    if (n <= 0) return null;
    var marked = Math.round(n * markup * 100) / 100;
    var formula = markup === 1 ? 'at cost' : ('\u00d7' + markup.toFixed(2));
    var profit = marginRate > 0 ? Math.round(n * marginRate * 100) / 100 : 0;
    return (
      <div key={label} style={{ display: 'grid',
        gridTemplateColumns: '1fr 60px 90px 80px', alignItems: 'baseline',
        gap: 8, fontSize: 11, padding: '4px 0' }}>
        <span style={{ color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
          {isOverride && (
            <span title="Manual override applied" style={{ marginLeft: 4, fontSize: 10 }}>
              {'\uD83D\uDD12'}
            </span>
          )}
          <span style={{ color: '#888', fontSize: 9, marginLeft: 6, fontFamily: 'monospace' }}>
            {fmt$(n)}
          </span>
        </span>
        <span style={{ color: '#888', fontSize: 9, fontFamily: 'monospace', textAlign: 'left' }}>
          {formula}
        </span>
        <span style={{ fontFamily: 'monospace', color: '#111', fontWeight: 500, textAlign: 'right' }}>
          {fmt$(marked)}
        </span>
        <span style={{ fontFamily: 'monospace', color: profit > 0 ? '#1A5C38' : '#bbb',
          fontSize: 10, textAlign: 'right' }}>
          {profit > 0 ? ('profit ' + fmt$(profit)) : '\u2014'}
        </span>
      </div>
    );
  }

  // Per-item fee rate (2026-10-06) — the same helper every invoice bills
  // from, so the tooltip cannot show a 10% formula on a 15% item.
  var feeRate = itemFeeMarkupPct(p.item) / 100;
  var totalProfit = itemFeeBreakdown(p.item).totals.markup;

  var content = (
    <div style={{ minWidth: 360 }}>
      <TooltipHeading>Fees breakdown</TooltipHeading>
      <div style={{ display: 'grid',
        gridTemplateColumns: '1fr 60px 90px 80px', gap: 8,
        fontSize: 8, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em',
        padding: '2px 0', borderBottom: '0.5px solid #EFEFEC' }}>
        <span>Fee</span>
        <span>Formula</span>
        <span style={{ textAlign: 'right' }}>Result</span>
        <span style={{ textAlign: 'right' }}>Profit</span>
      </div>
      {feeRow('Freight',       fees.freight,      1 + feeRate, feeRate)}
      {feeRow('Crating',       fees.crating,      1 + feeRate, feeRate)}
      {feeRow('Installation',  fees.installation, 1 + feeRate, feeRate)}
      {feeRow('Tariff',        fees.tariff,       1.00, 0)}
      {feeRow('Sales Tax',     fees.salesTax,     1.00, 0, taxOverride)}
      {(fees.custom || []).map(function(c, i) {
        var cr = customFeeMarkupPct(c, p.item) / 100;
        return feeRow(c.label || ('Custom ' + (i + 1)), c.amount, 1 + cr, cr);
      })}
      <div style={{ display: 'grid',
        gridTemplateColumns: '1fr 60px 90px 80px', alignItems: 'baseline',
        gap: 8, marginTop: 8, paddingTop: 8, borderTop: '0.5px solid #E0E0DC',
        fontSize: 11, fontWeight: 600 }}>
        <span style={{ color: '#111' }}>Total</span>
        <span />
        <span style={{ fontFamily: 'monospace', color: '#111', textAlign: 'right' }}>
          {fmt$(total)}
        </span>
        <span style={{ fontFamily: 'monospace', color: totalProfit > 0 ? '#1A5C38' : '#bbb',
          fontSize: 10, textAlign: 'right' }}>
          {totalProfit > 0 ? ('profit ' + fmt$(totalProfit)) : '\u2014'}
        </span>
      </div>
    </div>
  );

  return (
    <LedgerHoverTooltip content={content} width={400}>
      <span style={{ cursor: 'help', fontFamily: 'monospace', fontWeight: 600,
        fontSize: 13, color: '#111' }}>
        {fmt$(total)}
        <span style={{ marginLeft: 4, color: '#888', fontSize: 9 }}>{'\u25B8'}</span>
      </span>
    </LedgerHoverTooltip>
  );
}
